// src/components/chart/Summary.js
import React, { useEffect, useState, useCallback  } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import config from '../../config';
import { localData, usDate, Age, Gender, notify, postData, usPhoneFormat } from '../../functions';
import Header from '../common/Header';
import Footer from '../common/Footer';
import { PulseLoader } from 'react-spinners';

const Summary = () => {

  const goto = useNavigate();
  const patientData = localData('patient_data');
  const chartStatus = localData('chart_status');
  const medicationData = localData('medication_data');
  const reasonForVisitData = localData('reason_for_visit_data');
  const assessmentData = localData('assessment_data');
  const userData = localData('user');  
  const [practiceName, setPracticeName] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customHpi, setCustomHpi] = useState(localData('custom_hpi'));
  const [gptAssessment, setGptAssessment] = useState(localData('gpt_assessment'));
  

  const [firstLoad, setFirstLoad] = useState(false);
  const getFirstLoad = useCallback(async () => {

    // HPI Load
    if (firstLoad && !customHpi) { 
      const result = await postData(`${config.chartServerUrl}/openai/chat_hpi`, true, { chart_id: localData('chart_id'), ask:localData('raw_hpi') });
      if (result && result.status === 'success') {          
        setCustomHpi(result.response);
        localStorage.setItem('custom_hpi', result.response);
        console.log('%c ✔ HPI initialized successfully.', 'color: #68d100');
      } 
    }

    // Assesment Load
    if (firstLoad && !gptAssessment) { 
      let gptData = [];  
      for (const item of assessmentData) {
        try {
          if (!item.assessment || item.assessment === "Not Found") {
            gptData.push({
                icd_name: item.icd_name,
                icd_code: item.icd_code,
                assessment: item.assessment,
              });

          } else { 

            const result = await postData(`${config.chartServerUrl}/openai/chat_assessment`, true, {
              chart_id: localData('chart_id'),
              icd_code: item.icd_code,
              assessment: item.assessment
            });
            
            if (result && result.status === 'success') {
              gptData.push({
                icd_name: item.icd_name,
                icd_code: item.icd_code,
                assessment: result.response,
              });
            }

          }

        } catch (error) {
          console.error('Error fetching data:', error);
        }
      }
      setGptAssessment(gptData);
      localStorage.setItem('gpt_assessment', JSON.stringify(gptData));
      console.log('%c ✔ Assessment initialized successfully.', 'color: #68d100');
    }

    setFirstLoad(true); 
  
  }, [firstLoad, assessmentData, gptAssessment, customHpi ]);

  useEffect(() => {
    if (!medicationData || !reasonForVisitData || !patientData || !assessmentData) {
      goto('/waiting-room/reason');   
    }

    const practice = userData.practice_info.find(info => info.practice_id === localData('practice_id'))
    if (practice) {
      setPracticeName(practice.practice_name);
      getFirstLoad();
    }
    
  }, [goto, medicationData, reasonForVisitData, patientData, assessmentData, userData, getFirstLoad]);


  const updateStatus = async () => {
    setIsSubmitting(true);
    const result = await postData(`${config.chartServerUrl}/store/chart_status`, true, { chart_id: localData("chart_id"), status: 2 });
    if (result) {
      notify(result.status, result.message)
      setIsSubmitting(false);
      goto('/waiting-room');   
    } 
  };

  
  
  let diagnosisNumberCounter = 0;
  return (
    <>
      <Helmet>
        <title>Summary | { config.appName}</title>
        <meta name="description" content="Summary Page Description" />        
      </Helmet>

      <Header />

      <div className="container mx-auto mt-7 pb-28 px-2" >
        <div className="relative isolate">

          <div className="grid grid-cols-1 gap-4">
            <div className={`${chartStatus === 1 ? 'block' : 'hidden'}`}>
              <ol className="flex items-center w-full text-sm font-medium text-center text-gray-500 dark:text-gray-400 sm:text-base">

                <li className="flex md:w-full items-center text-indigo-600 dark:text-indigo-500 sm:after:content-[''] after:w-full after:h-1 after:border-b after:border-indigo-200 after:border-1 after:hidden sm:after:inline-block after:mx-6 xl:after:mx-10 dark:after:border-gray-700">
                  <Link to="/waiting-room/reason">    
                    <span className="flex items-center after:content-['/'] sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
                        </svg>
                        Reason <span className="hidden sm:inline-flex sm:ms-2"></span>
                    </span>
                  </Link>
                </li>             

                <li className="flex items-center text-indigo-600 dark:text-indigo-500">
                  <Link to="/waiting-room/reason/summary">
                  <span className="flex items-center  sm:after:hidden after:mx-2 after:text-gray-200 dark:after:text-gray-500">
                        <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 me-2" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5Zm3.707 8.207-4 4a1 1 0 0 1-1.414 0l-2-2a1 1 0 0 1 1.414-1.414L9 10.586l3.293-3.293a1 1 0 0 1 1.414 1.414Z"/>
                        </svg>
                        Summary <span className="hidden sm:inline-flex sm:ms-2"></span>
                  </span>
                  </Link>
                </li>   

              </ol>
            </div>
            <div>
              <div className="space-y-10 divide-y divide-gray-900/10">
              
                <div className="grid grid-cols-1 gap-x-8 gap-x gap-y-8 pt-7 md:grid-cols-3">
                  
                  {/* New Data */}                
                  <div className="sm:col-span-full">
                    <div className="w-full p-4 bg-white border border-gray-200 rounded-lg shadow sm:p-8 dark:bg-gray-800 dark:border-gray-700 ">
                      <div className="text-center mb-10">                      
                        <h5 className="mb-1 text-3xl font-bold text-indigo-600 dark:text-indigo-400 uppercase mt-8">{ practiceName}</h5>
                          {/* <b className="mb-5 text-base text-gray-700 dark:text-gray-300 sm:text-lg">Baraya, Edgar F, MD</b> */}
                      </div>
                        
                      <div className="grid grid-cols-2 lg:grid-cols-2 md:grid-cols-2 gap-2 uppercase">
                        <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> Name : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> {patientData ? patientData.first_name+', '+patientData.last_name : null}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> GENDER : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> { patientData ? Gender(patientData.sex) : null}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> DOB : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> { patientData ? usDate(patientData.dob) : null}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> AGE : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> { patientData ? Age(patientData.dob) : null}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> PHONE : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> { patientData ? usPhoneFormat(patientData.phone) : null}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> DOS : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> 12/18/2023</span>
                        </div>
                        {/* <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> Time : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> 09:10:00 AM</span>
                        </div> */}
                        <div className='col-span-2 xl:col-span-1'>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> Insurance Name : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> { patientData ? patientData.insurance_name : null}</span>
                        </div>
                        <div className='col-span-2 xl:col-span-1'>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> Insurance ID : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> { patientData ? patientData.insurance : null}</span>
                        </div>
                        {/* <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> Insured : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> {patientData ? patientData.first_name+', '+patientData.last_name : null}</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> Insured ID : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> 132107227</span>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-900 truncate dark:text-white"> Policy Number : </span>
                          <span className="text-sm text-gray-500 truncate dark:text-gray-400"> 0000</span>
                        </div> */}
                        
                      </div>
                      <div className='mt-10'>
                        <h5 className="mb-2 text-1xl font-bold text-indigo-600 dark:text-indigo-400 uppercase">Chief Complaint</h5>
                        <div className='ml-5'>
                          
                          {/* Reason for Visit */}
                          <h5 className="mb-2 text-1xl font-bold text-gray-600 dark:text-gray-300 uppercase">Reason for Visit:</h5>
                          <ul className='ml-5 list-disc list-inside '>
                           {reasonForVisitData && Object.values(reasonForVisitData.rfvData).map((rfv, index) => (
                            <li key={index}>
                              {rfv.rfv_name} 
                              {rfv.extra_notes ? <p className='text-indigo-400 ml-5 text-sm pl-1'>{rfv.extra_notes}</p> : ''}
                            </li>
                          ))}

                          </ul>

                          {/* History of Present Illness: */}
                          <h5 className="mb-2 text-1xl font-bold text-gray-600 dark:text-gray-300 uppercase mt-3">History of Present Illness:</h5>
                          <div className='ml-5'>  
                            <div className='whitespace-break-spaces'>
                              <div className={`h-16  flex items-center justify-center ${customHpi ? 'hidden' : 'block'}`}>                 
                                <div>
                                    <PulseLoader color="#4f46e5" />
                                </div>
                            </div>                              
                            {customHpi}

                            </div>                            
                            <p className='mt-2'>
                              Any Hospitalization Since Last Visit: None<br></br>
                              Any Self Referrals Since Last Visit: None<br></br>
                              How would you like your medicine to be dispensed: Tablet<br></br>
                              Old chart and medical record reviewed<br></br>
                              Lab work, Imaging studies reviewed and discussed with patient

                            </p>     
                          </div> 
                        </div>

                        {/* Diagnosis Code */}
                        <h5 className="mb-2 text-1xl font-bold text-indigo-600 dark:text-indigo-400 uppercase mt-10">Diagnosis Code:</h5>
                        <div className='ml-5'>                       
                          {medicationData && Object.values(medicationData).map((rfvItem, index) => (
                            <div key={index}>
                              {rfvItem.icd_data && (
                                <ul className='list-inside '>
                                  {rfvItem.icd_data.map((icdItem) => (
                                    <li key={icdItem.icd_id}>
                                      <b>{`${++diagnosisNumberCounter}.`}</b> {icdItem.icd_name} ({icdItem.icd_code})
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>

                        {/* Assessment/Plan */}
                        <h5 className="mb-2 text-1xl font-bold text-indigo-600 dark:text-indigo-400 uppercase mt-10">Assessment/Plan:</h5>
                        <div className='ml-5'>
                          <div className={`h-16  flex items-center justify-center ${gptAssessment ? 'hidden' : 'block'}`}>                 
                              <div>
                                  <PulseLoader color="#4f46e5" />
                              </div>
                          </div> 
                          {gptAssessment && gptAssessment.map((assessment) => (
                            <div key={assessment.icd_code}>
                              <h5 className="mb-2 text-1xl font-bold text-gray-600 dark:text-gray-300 uppercase mt-3">{assessment.icd_name}</h5>
                              <div className='ml-5 whitespace-break-spaces'>{assessment.assessment}</div>
                            </div>
                          ))}
                        </div>
                      
                        {/* Review of Systems */}
                        <h5 className="mb-2 text-1xl font-bold text-indigo-600 dark:text-indigo-400 uppercase mt-10">Review of Systems:</h5>

                        {/* <div className='ml-5'>     
                          {medicationData && Object.values(medicationData).map((rfvItem, index) => (
                            <div key={index}>
                              <h5 className="mb-2 text-1xl font-bold text-gray-600 dark:text-gray-300 uppercase mt-3">{rfvItem.ros_name}</h5>
                              <div className='ml-5'>{rfvItem.ros_details}</div>                             
                            </div>
                          ))}
                        </div> */}

                        <div className='ml-5'>                       
                          {medicationData && Object.values(medicationData).map((rfvItem, index) => (
                            <div key={index}>
                              {rfvItem.ros_data && (
                                <>                               
                                  {rfvItem.ros_data.map((rosItem) => (                                    
                                  <div key={rosItem.ros_id}>
                                    <h5 className="mb-2 text-1xl font-bold text-gray-600 dark:text-gray-300 uppercase mt-3">{rosItem.ros_name}</h5>
                                    <div className='ml-5 whitespace-break-spaces'>{rosItem.ros_details}</div>
                                  </div>
                                  ))}
                                </> 
                              )}
                            </div>
                          ))}
                        </div>


                      
                        {/* Physical Exam */}
                        <h5 className="mb-2 text-1xl font-bold text-indigo-600 dark:text-indigo-400 uppercase mt-10">Physical Exam:</h5>
                        {/* <div className='ml-5 mb-10'>                       
                          {medicationData && Object.values(medicationData).map((rfvItem, index) => (
                            <div key={index}>
                              <h5 className="mb-2 text-1xl font-bold text-gray-600 dark:text-gray-300 uppercase mt-3">{rfvItem.pe_name}</h5>
                              <div className='ml-5'>{rfvItem.pe_details}</div>
                            </div>
                          ))}
                        </div> */}

                        <div className='ml-5'>                       
                          {medicationData && Object.values(medicationData).map((rfvItem, index) => (
                            <div key={index}>
                              {rfvItem.pe_data && (
                                <>                               
                                  {rfvItem.pe_data.map((peItem) => (
                                    <div key={peItem.pe_id}>
                                      <h5 className="mb-2 text-1xl font-bold text-gray-600 dark:text-gray-300 uppercase mt-3">{peItem.pe_name}</h5>
                                      <div className='ml-5 whitespace-break-spaces'>{peItem.pe_details}</div>
                                    </div>
                                  ))}
                                </> 
                              )}
                            </div>
                          ))}
                        </div>
                      
                      </div> 
                    </div>
                    <div className={`${chartStatus === 1 ? 'block' : 'hidden'}`}>
                      <div className="flex items-center justify-between gap-x-6 mb-10 px-4 py-4 sm:px-8">                  
                        <Link to="/waiting-room/reason" className=" bg-slate-400 hover:bg-slate-600 text-white  py-2 px-4 rounded inline-flex items-center">
                        
                          <svg className="w-4 h-3 mr-2 text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 1 1 5l4 4"/>
                          </svg>
                          <span>Back</span>
                        </Link>
                      

                        <button type="button" onClick={updateStatus} disabled={isSubmitting} className="bg-indigo-500 hover:bg-indigo-600 text-white  py-2 px-4 rounded inline-flex items-center">
                          <span>Done</span>
                          <svg className={`w-3 h-3 ml-3 text-white ${isSubmitting ? 'hidden' : ''}`} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 6 10">
                            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 9 4-4-4-4"/>
                          </svg>             

                          <svg className={`w-4 h-4 ml-3 text-white animate-spin ${isSubmitting ? '' : 'hidden'}`} aria-hidden="true"  role="status" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#E5E7EB"/>
                            <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
                          </svg>
                        </button>
                        
                      </div>
                    </div>

                  </div>
      
                  


                </div>   
              </div>

            </div>
          </div>

        </div>
      </div>

      <Footer />      
    </>
  );
};

export default Summary;
